import React from "react";
import { Item, Enemy } from "../types";
import HealthBar from "./HealthBar";

interface ClickAreaProps {
  onClick: () => void;
  clicking: boolean;
  lastDrop: (Item & { quantity: number }) | null;
  currentEnemy: Enemy | null;
  currentEnemyHealth: number;
  damageNumbers: Array<{ id: number; damage: number; timestamp: number }>;
  enemyShakeType?: "damage" | "death" | null;
}

const ClickArea: React.FC<ClickAreaProps> = ({
  onClick,
  clicking,
  lastDrop,
  currentEnemy,
  currentEnemyHealth,
  damageNumbers,
  enemyShakeType,
}) => {
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case "common":
        return "#9ca3af";
      case "rare":
        return "#3b82f6";
      case "epic":
        return "#8b5cf6";
      case "legendary":
        return "#f59e0b";
      default:
        return "#9ca3af";
    }
  };

  return (
    <div className="click-area">
      {currentEnemy && (
        <div className="enemy-encounter">
          <HealthBar
            currentHealth={currentEnemyHealth}
            maxHealth={currentEnemy.max_health}
            enemyName={currentEnemy.name}
            rarity={currentEnemy.rarity}
          />

          <div className="enemy-display">
            <div className="enemy-image-container">
              <img
                key={currentEnemy.id}
                src={currentEnemy.image_url}
                alt={currentEnemy.name}
                className={`enemy-image ${
                  enemyShakeType === "death"
                    ? "enemy-shake-death"
                    : enemyShakeType === "damage"
                    ? "enemy-shake"
                    : ""
                }`}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fallback = e.currentTarget
                    .nextElementSibling as HTMLElement | null;
                  if (fallback) {
                    fallback.style.display = "block";
                  }
                }}
              />
              <div className="enemy-fallback" style={{ display: "none" }}>
                {currentEnemy.name.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="click-zone" onClick={onClick}>
        <div className={`click-button ${clicking ? "clicking" : ""}`}>
          <div className="click-icon">⚔️</div>
          <div className="click-text">
            {clicking ? "Attacking..." : "ATTACK!"}
          </div>
          {damageNumbers.map((damageData, index) => {
            // Add some random horizontal offset for natural stacking
            const randomOffset = (Math.random() - 0.5) * 20;
            return (
              <div
                key={damageData.id}
                className="damage-indicator"
                style={{
                  top: `${-40 - index * 15}px`,
                  left: `calc(50% + ${randomOffset}px)`,
                  zIndex: 100 + index,
                  animationDelay: `${index * 0.05}s`,
                }}
              >
                -{damageData.damage}
              </div>
            );
          })}
        </div>
      </div>

      {lastDrop && (
        <div className="drop-notification">
          <div className="drop-header">
            <img src="/retomon-logo.jpg" alt="RETOMON" className="drop-logo" />
            <div className="drop-title">ITEM DROPPED!</div>
          </div>
          <div
            className="drop-item"
            style={{ borderColor: getRarityColor(lastDrop.rarity) }}
          >
            <div className="drop-image">
              <img
                src={lastDrop.image_url}
                alt={lastDrop.name}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fallback = e.currentTarget
                    .nextElementSibling as HTMLElement | null;
                  if (fallback && "style" in fallback) {
                    fallback.style.display = "block";
                  }
                }}
              />
              <div className="drop-fallback" style={{ display: "none" }}>
                {lastDrop.name.charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="drop-details">
              <div
                className="drop-name"
                style={{ color: getRarityColor(lastDrop.rarity) }}
              >
                {lastDrop.name}
              </div>
              <div className="drop-quantity">x1</div>
            </div>
          </div>
        </div>
      )}

      <div className="click-instructions">
        <p>Attack the RETOMON to defeat it!</p>
        <p>Defeated RETOMONs can drop items!</p>
        <div className="keyboard-hint">
          <span className="key">SPACE</span> or{" "}
          <span className="key">ENTER</span> to attack
        </div>
      </div>
    </div>
  );
};

export default ClickArea;
